import { useCallback, useEffect, useRef, useState } from "react";

/** @typedef {{ left: number, top: number, width: number, height: number }} ImageRect */

/**
 * @param {HTMLImageElement} img
 * @returns {ImageRect | null}
 */
function getContainedImageRect(img) {
  const { naturalWidth, naturalHeight, width, height } = img;
  if (!naturalWidth || !naturalHeight || !width || !height) {
    return null;
  }

  const scale = Math.min(width / naturalWidth, height / naturalHeight);
  const displayWidth = naturalWidth * scale;
  const displayHeight = naturalHeight * scale;

  return {
    left: (width - displayWidth) / 2,
    top: (height - displayHeight) / 2,
    width: displayWidth,
    height: displayHeight,
  };
}

/**
 * @param {ImageRect} rect
 * @param {number} clientX
 * @param {number} clientY
 * @param {DOMRect} frameRect
 */
function pointToFocus(rect, clientX, clientY, frameRect) {
  const x = clientX - frameRect.left - rect.left;
  const y = clientY - frameRect.top - rect.top;
  const focusX = Math.max(0, Math.min(100, (x / rect.width) * 100));
  const focusY = Math.max(0, Math.min(100, (y / rect.height) * 100));
  return { focusX: Math.round(focusX * 10) / 10, focusY: Math.round(focusY * 10) / 10 };
}

/**
 * @param {{ src: string, alt?: string, focusX: number, focusY: number, onChange: (focus: { focusX: number, focusY: number }) => void }} props
 */
export default function FocalPointPicker({ src, alt = "", focusX, focusY, onChange }) {
  const frameRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const imgRef = useRef(/** @type {HTMLImageElement | null} */ (null));
  const draggingRef = useRef(false);
  const [imageRect, setImageRect] = useState(/** @type {ImageRect | null} */ (null));

  const updateImageRect = useCallback(() => {
    const img = imgRef.current;
    if (!img) {
      return;
    }
    setImageRect(getContainedImageRect(img));
  }, []);

  useEffect(() => {
    const img = imgRef.current;
    if (!img) {
      return undefined;
    }

    updateImageRect();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateImageRect);
      return () => window.removeEventListener("resize", updateImageRect);
    }

    const observer = new ResizeObserver(updateImageRect);
    observer.observe(img);
    return () => observer.disconnect();
  }, [src, updateImageRect]);

  const setFocusFromPointer = useCallback(
    (clientX, clientY) => {
      const frame = frameRef.current;
      const img = imgRef.current;
      if (!frame || !img) {
        return;
      }

      const rect = getContainedImageRect(img);
      if (!rect) {
        return;
      }

      onChange(pointToFocus(rect, clientX, clientY, frame.getBoundingClientRect()));
    },
    [onChange]
  );

  useEffect(() => {
    function handlePointerMove(event) {
      if (!draggingRef.current) {
        return;
      }
      event.preventDefault();
      setFocusFromPointer(event.clientX, event.clientY);
    }

    function handlePointerUp() {
      draggingRef.current = false;
    }

    window.addEventListener("pointermove", handlePointerMove);
    window.addEventListener("pointerup", handlePointerUp);
    window.addEventListener("pointercancel", handlePointerUp);

    return () => {
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerUp);
      window.removeEventListener("pointercancel", handlePointerUp);
    };
  }, [setFocusFromPointer]);

  function handlePointerDown(event) {
    if (event.button !== 0) {
      return;
    }
    event.preventDefault();
    draggingRef.current = true;
    setFocusFromPointer(event.clientX, event.clientY);
  }

  const overlayStyle = imageRect
    ? {
        left: `${imageRect.left}px`,
        top: `${imageRect.top}px`,
        width: `${imageRect.width}px`,
        height: `${imageRect.height}px`,
      }
    : { display: "none" };

  return (
    <div className="admin-focal-picker">
      <p className="admin-label">Crop anchor — where to keep centered as the screen gets smaller</p>
      <div className="admin-focal-picker__frame" ref={frameRef}>
        <img
          ref={imgRef}
          className="admin-focal-picker__image"
          src={src}
          alt={alt}
          onLoad={updateImageRect}
          draggable={false}
        />
        <div
          className="admin-focal-picker__overlay"
          style={overlayStyle}
          onPointerDown={handlePointerDown}
          role="presentation"
        >
          <div className="admin-focal-picker__crosshair-h" style={{ top: `${focusY}%` }} aria-hidden="true" />
          <div className="admin-focal-picker__crosshair-v" style={{ left: `${focusX}%` }} aria-hidden="true" />
          <div
            className="admin-focal-picker__dot"
            style={{ left: `${focusX}%`, top: `${focusY}%` }}
            aria-hidden="true"
          />
        </div>
      </div>
      <p className="admin-hint">
        Click or drag the red dot to set the crop anchor ({focusX.toFixed(1)}%, {focusY.toFixed(1)}%).
      </p>
    </div>
  );
}
